import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Flame, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import AnswerView from "@/components/AnswerView";
import ShareCardModal from "@/components/ShareCardModal";
import DonateModal from "@/components/DonateModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedAnswerPage() {
  const { id } = useParams();
  const [state, setState] = useState("loading"); // loading | loaded | not_found | error
  const [entry, setEntry] = useState(null);
  const [shareEntry, setShareEntry] = useState(null);
  const [donateOpen, setDonateOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/share/${id}`);
        if (cancelled) return;
        setEntry({
          id: res.data.id,
          shareId: res.data.id,
          question: res.data.question,
          answer: res.data.answer,
          faith: res.data.faith,
          status: "done",
        });
        setState("loaded");
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) setState("not_found");
        else setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  return (
    <>
      <Header onOpenDonate={() => setDonateOpen(true)} />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14 relative z-10">
        {state === "loading" && (
          <div
            data-testid="shared-answer-loading"
            className="flex items-center gap-3 text-[#FBEBD3] italic font-scripture text-lg"
          >
            <Flame className="w-5 h-5 text-[#E7B04A] flicker" />
            <span>Opening the scroll…</span>
          </div>
        )}

        {state === "not_found" && (
          <div
            data-testid="shared-answer-not-found"
            className="rounded-2xl gold-border bg-[#4A0A17]/60 p-8 sm:p-10 text-center"
          >
            <h1 className="font-serif-display text-3xl text-[#FFFFFF] mb-3">
              This passage has drifted away.
            </h1>
            <p className="font-scripture italic text-[#FBEBD3] mb-6">
              The shared answer could not be found — it may have expired or the link
              was mistyped.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full bg-[#E7B04A] hover:bg-[#F5C76B] px-5 py-2.5 text-sm uppercase tracking-[0.14em] font-bold text-[#5C0E1D] transition-colors"
            >
              Ask your own question
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {state === "error" && (
          <div
            data-testid="shared-answer-error"
            className="rounded-2xl gold-border bg-[#4A0A17]/60 p-8 sm:p-10 text-center"
          >
            <p className="font-scripture italic text-[#FBEBD3]">
              Something went wrong opening this answer. Please try again in a moment.
            </p>
          </div>
        )}

        {state === "loaded" && entry && (
          <>
            <AnswerView
              index={0}
              entry={entry}
              onShare={(e) => setShareEntry(e)}
            />
            <div className="mt-8 text-center">
              <Link
                to="/"
                data-testid="shared-answer-ask-your-own"
                className="inline-flex items-center gap-2 rounded-full bg-[#E7B04A] hover:bg-[#F5C76B] px-6 py-3 text-sm uppercase tracking-[0.14em] font-bold text-[#5C0E1D] transition-colors shadow-md"
              >
                Ask your own question
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        )}
      </main>
      <DonateModal open={donateOpen} onClose={() => setDonateOpen(false)} />
      <ShareCardModal
        open={!!shareEntry}
        onClose={() => setShareEntry(null)}
        question={shareEntry?.question || ""}
        answer={shareEntry?.answer || ""}
        shareId={shareEntry?.shareId}
      />
    </>
  );
}
